import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  Autocomplete,
  Link,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  AccountTree as AccountTreeIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { groupsAPI } from '../services/api';
import {
  fetchGroups,
  fetchUsers,
  fetchGroupMembers,
  addGroupOptimistic,
  removeGroupOptimistic,
} from '../store/dataSlice';
import UserDetailsDialog from '../components/UserDetailsDialog';
import PaginationControls from '../components/PaginationControls';

import DataSource from '../components/DataSource';
import { formatCreated } from '../utils/formatCreated';
import ColumnPicker from '../components/ColumnPicker';
import { useColumnPrefs } from '../hooks/useColumnPrefs';
function Groups() {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.data.users.data);
  const usersLoading = useSelector((state) => state.data.users.loading);

  // Filter out TLS configuration warnings that appear as "groups"
  const TLS_WARNINGS = [
    'Global parameter tls enabled found in service section!',
    'Global parameter tls keyfile found in service section!',
    'Global parameter tls certfile found in service section!',
    'Global parameter tls cafile found in service section!',
  ];

  // Pagination state
  const [groups, setGroups] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  // Which DC actually served this listing.
  const [dataSource, setDataSource] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [currentGroup, setCurrentGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedParentGroups, setSelectedParentGroups] = useState([]);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [selectedUsername, setSelectedUsername] = useState(null);

  // Search and sort state
  const [searchQuery, setSearchQuery] = useState('');

  const GROUP_COLUMNS = React.useMemo(() => [
    { key: 'name', label: 'Name', render: (g) => g.name },
    { key: 'description', label: 'Description', render: (g) => g.description || '-' },
    { key: 'created', label: 'Created', sx: { whiteSpace: 'nowrap' },
      render: (g) => formatCreated(g.whenCreated) },
    { key: 'dn', label: 'Distinguished Name',
      sx: { fontFamily: 'monospace', fontSize: '0.8125rem' },
      render: (g) => g.dn || '-' },
  ], []);
  const { ordered, visibleColumns, toggle, move, reset } =
    useColumnPrefs('cols.groups', GROUP_COLUMNS);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const loadGroups = async (currentPage = page, currentPageSize = pageSize, refresh = false) => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: currentPage,
        page_size: currentPageSize,
      };
      if (refresh) {
        params.refresh = 'true';
      }
      const response = await groupsAPI.list(params);
      let allGroupsData = response.data.results || [];

      // Filter out TLS warnings
      allGroupsData = allGroupsData.filter(group => !TLS_WARNINGS.includes(group.name));

      // Apply search filter (client-side for now)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        allGroupsData = allGroupsData.filter(group =>
          (group.name && group.name.toLowerCase().includes(query)) ||
          (group.description && group.description.toLowerCase().includes(query))
        );
      }

      // Apply sorting (client-side)
      allGroupsData.sort((a, b) => {
        let aVal = a[sortBy] || '';
        let bVal = b[sortBy] || '';
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();

        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });

      setGroups(allGroupsData);
      setTotalCount(allGroupsData.length);
      setDataSource(response.data.source || null);
      setTotalPages(Math.ceil(allGroupsData.length / currentPageSize));
      setPage(currentPage);
      setPageSize(currentPageSize);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load groups with pagination
    loadGroups(1, pageSize);
    // Load users
    dispatch(fetchUsers());

    // Listen for sudo session established event
    const handleSudoSession = () => {
      // Force refresh when sudo session established
      loadGroups(page, pageSize, true);
      dispatch(fetchUsers(true));
    };

    window.addEventListener('sudo-session-established', handleSudoSession);

    return () => {
      window.removeEventListener('sudo-session-established', handleSudoSession);
    };
  }, []);

  // Reload when search or sort changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadGroups(1, pageSize);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, sortBy, sortOrder]);

  const handleRefresh = () => {
    // Force refresh bypassing cache
    loadGroups(page, pageSize, true);
    dispatch(fetchUsers(true));
  };

  const handlePageChange = (event, newPage) => {
    loadGroups(newPage, pageSize);
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = event.target.value;
    loadGroups(1, newPageSize); // Reset to page 1 when changing page size
  };

  const handleUserClick = (username) => {
    setSelectedUsername(username);
    setUserDetailsOpen(true);
  };

  const handleUserDetailsClose = () => {
    setUserDetailsOpen(false);
    setSelectedUsername(null);
  };

  const handleOpenDialog = async (mode, group = null) => {
    setDialogMode(mode);
    setCurrentGroup(group);
    setSelectedUsers([]);
    setSelectedParentGroups([]);

    if (mode === 'create') {
      setFormData({ name: '', description: '' });
    } else if (mode === 'members' && group) {
      try {
        // Fetch members (will use cache if available)
        const response = await groupsAPI.getMembers(group.name);
        setGroupMembers(response.data.members || []);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load members');
      }
    } else if (mode === 'nest-group' && group) {
      // Initialize for nest-group mode
      setSelectedParentGroups([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentGroup(null);
    setGroupMembers([]);
    setSelectedUsers([]);
    setSelectedParentGroups([]);
    setFormData({ name: '', description: '' });
  };

  const handleSubmit = async () => {
    if (dialogMode === 'create') {
      if (!formData.name || formData.name.trim() === '') {
        setError('Group name is required');
        return;
      }
      if (!/^[a-zA-Z0-9_-]+$/.test(formData.name)) {
        setError('Group name can only contain letters, numbers, hyphens, and underscores');
        return;
      }
    }

    if (dialogMode === 'add-members' && selectedUsers.length === 0) {
      setError('Please select at least one user to add');
      return;
    }

    if (dialogMode === 'remove-members' && selectedUsers.length === 0) {
      setError('Please select at least one member to remove');
      return;
    }

    if (dialogMode === 'nest-group' && selectedParentGroups.length === 0) {
      setError('Please select at least one parent group');
      return;
    }

    try {
      if (dialogMode === 'create') {
        await groupsAPI.create(formData);
        // Optimistic update
        dispatch(addGroupOptimistic({
          name: formData.name,
          description: formData.description,
        }));
      } else if (dialogMode === 'add-members') {
        const usernames = selectedUsers.map((u) => u.username);
        await groupsAPI.addMembers(currentGroup.name, usernames);
      } else if (dialogMode === 'remove-members') {
        const usernames = selectedUsers.map((u) => u);
        await groupsAPI.removeMembers(currentGroup.name, usernames);
      } else if (dialogMode === 'nest-group') {
        const parentGroupNames = selectedParentGroups.map((g) => g.name);
        await groupsAPI.nestGroup(currentGroup.name, parentGroupNames);
      }
      handleCloseDialog();
      // Refresh to ensure data is accurate
      loadGroups(page, pageSize, true);
      if (dialogMode !== 'create') {
        dispatch(fetchUsers(true));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Operation failed');
      // Refresh on error
      loadGroups(page, pageSize, true);
    }
  };

  const handleDelete = async (groupName) => {
    if (!window.confirm(`Are you sure you want to delete group ${groupName}?`)) {
      return;
    }
    try {
      await groupsAPI.delete(groupName);
      // Refresh after deletion
      loadGroups(page, pageSize, true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete group');
      // Refresh on error
      loadGroups(page, pageSize, true);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3,
                 flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4">Groups</Typography>
          <DataSource source={dataSource} sx={{ mt: 0.5 }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ColumnPicker columns={ordered} onToggle={toggle} onMove={move} onReset={reset} />
          <IconButton onClick={handleRefresh} sx={{ mr: 1 }} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('create')}
          >
            Create Group
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Search and Filter Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search groups by name or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {searchQuery && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Found {totalCount} group{totalCount !== 1 ? 's' : ''} matching "{searchQuery}"
            </Typography>
          </Box>
        )}
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {visibleColumns.map((c) => (
                <TableCell key={c.key}>{c.label}</TableCell>
              ))}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length + 1} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No groups found. Click "Create Group" to add a new group.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              groups.map((group) => (
              <TableRow key={group.name}>
                {visibleColumns.map((c) => (
                  <TableCell key={c.key} sx={c.sx}>{c.render(group)}</TableCell>
                ))}
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog('members', group)}
                    title="View Members"
                  >
                    <GroupIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog('nest-group', group)}
                    title="Nest A Group"
                  >
                    <AccountTreeIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(group.name)}
                    title="Delete"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PaginationControls
        count={totalCount}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' && 'Create Group'}
          {dialogMode === 'members' && `Members of ${currentGroup?.name}`}
          {dialogMode === 'add-members' && 'Add Members'}
          {dialogMode === 'remove-members' && 'Remove Members'}
          {dialogMode === 'nest-group' && `Nest Group: ${currentGroup?.name}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {dialogMode === 'create' && (
              <>
                <TextField
                  fullWidth
                  label="Group Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  margin="normal"
                  multiline
                  rows={3}
                />
              </>
            )}
            {dialogMode === 'members' && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<PersonAddIcon />}
                    onClick={() => {
                      setDialogMode('add-members');
                    }}
                    sx={{ mr: 1 }}
                  >
                    Add Members
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<PersonRemoveIcon />}
                    onClick={() => {
                      setDialogMode('remove-members');
                    }}
                  >
                    Remove Members
                  </Button>
                </Box>
                <List>
                  {groupMembers.length === 0 ? (
                    <ListItem>
                      <ListItemText primary="No members" />
                    </ListItem>
                  ) : (
                    groupMembers.map((member) => (
                      <ListItem key={member}>
                        <ListItemText
                          primary={
                            <Link
                              component="button"
                              variant="body1"
                              onClick={() => handleUserClick(member)}
                              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                            >
                              {member}
                            </Link>
                          }
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              </>
            )}
            {dialogMode === 'add-members' && (
              <Autocomplete
                multiple
                options={users.filter(
                  (u) => !groupMembers.includes(u.username)
                )}
                getOptionLabel={(option) => option.username}
                value={selectedUsers}
                onChange={(event, newValue) => setSelectedUsers(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Select Users" margin="normal" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.username}
                      {...getTagProps({ index })}
                      key={option.username}
                    />
                  ))
                }
              />
            )}
            {dialogMode === 'remove-members' && (
              <Autocomplete
                multiple
                options={groupMembers}
                getOptionLabel={(option) => option}
                value={selectedUsers}
                onChange={(event, newValue) => setSelectedUsers(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Select Members to Remove" margin="normal" />
                )}
              />
            )}
            {dialogMode === 'nest-group' && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Add the group "{currentGroup?.name}" to one or more parent groups. This will make "{currentGroup?.name}" a member of the selected groups.
                </Typography>
                <Autocomplete
                  multiple
                  options={groups.filter((g) => g.name !== currentGroup?.name)}
                  getOptionLabel={(option) => option.name}
                  value={selectedParentGroups}
                  onChange={(event, newValue) => setSelectedParentGroups(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Parent Groups"
                      margin="normal"
                      helperText="Choose one or more groups to add this group to"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name}
                        {...getTagProps({ index })}
                        key={option.name}
                      />
                    ))
                  }
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {dialogMode === 'members' ? 'Close' : 'Cancel'}
          </Button>
          {dialogMode !== 'members' && (
            <Button onClick={handleSubmit} variant="contained">
              {dialogMode === 'create' ? 'Create' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <UserDetailsDialog
        open={userDetailsOpen}
        onClose={handleUserDetailsClose}
        username={selectedUsername}
      />
    </Box>
  );
}

export default Groups;
